import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, FileText, AlertCircle, CheckCircle, Edit2, Link2, Copy, LogOut, ArrowLeft, ArrowRight, Plus, X, ExternalLink, ThumbsUp, RefreshCcw, Send, HelpCircle, ChevronDown, Download, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { API_ROOT } from "@/config";
import BrandCampaignsSidebar from "@/components/ui/brand-campaigns-sidebar";
import FeatureRequestButton from "@/components/ui/feature-request-button";
import SEO from "@/components/seo";
import { AgGridReact } from 'ag-grid-react';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { createBrandCampaignTour, tourStyles, hasSeenIntroTour, markIntroTourShown } from "@/components/ui/tour-utils";
import useSubscription from "@/hooks/useSubscription";
import UsageWarning from "@/components/ui/usage-warning";

const PALETTE = {
  inchworm: '#B1FA63',
  gunmetal: '#243837',
  orange: '#FE7733',
  paleViolet: '#B2A1FF',
  brightSnow: '#FFFFFF',
  americanSilver: '#D1D1D1',
};

interface ExtractedBriefingData {
  campaign_name: string;
  brand_name: string;
  product_name: string;
  hooks: string[];
  hooks_must_be_present: boolean;
  topics: string[];
  topics_must_be_present: boolean;
  call_to_actions: string[];
  call_to_actions_must_be_present: boolean;
  elements: string[];
  elements_must_be_present: boolean;
  story: string;
  story_must_be_present: boolean;
  visuals_background: string;
  visuals_background_must_be_present: boolean;
  brand_logo_path?: string;
  brand_logo_must_be_present: boolean;
  product_image_path?: string;
  product_image_must_be_present: boolean;
}

interface UserInfo {
  email: string;
  first_name: string;
  surname: string;
  company_name?: string;
  user_type?: string;
}

const GRID_AUTO_SIZE_STRATEGY = { type: 'fitGridWidth' as const };
const GRID_DEFAULT_COL_DEF = {
  sortable: true,
  filter: false,
  resizable: false,
  suppressMovable: true,
  tooltipComponent: undefined,
};
const GRID_INITIAL_STATE = {
  sort: { sortModel: [{ colId: 'submitted_at', sort: 'desc' as const }] },
};
const GRID_NO_ROWS_TEMPLATE = "<span style='color: #243837; font-size: 14px;'>No Submissions</span>";

const BrandCampaignManager: React.FC = () => {
  const navigate = useNavigate();
  const { subscription, isLoading: subLoading, refresh: refreshSubscription } = useSubscription();
  const [step, setStep] = useState<'upload' | 'review' | 'published'>('upload');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  
  // Upload step
  const [mode, setMode] = useState<'upload' | 'paste'>('upload');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [pastedText, setPastedText] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressStage, setProgressStage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [timerProgress, setTimerProgress] = useState(0);
  
  // Review step
  const [extractedData, setExtractedData] = useState<ExtractedBriefingData | null>(null);
  const [isPublishing, setIsPublishing] = useState(false);
  
  // Published step
  const [campaignLink, setCampaignLink] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [selectedCampaignUuid, setSelectedCampaignUuid] = useState<string | null>(null);
  const [selectedCampaign, setSelectedCampaign] = useState<any>(null);
  const [copyLinkSuccess, setCopyLinkSuccess] = useState(false);
  const [sidebarRefreshTrigger, setSidebarRefreshTrigger] = useState(0);
  const [trialBannerDismissed, setTrialBannerDismissed] = useState(() => sessionStorage.getItem('trial_banner_dismissed') === 'true');
  const [showTrialWelcome, setShowTrialWelcome] = useState(false);

  // Modal states for Approval/Revision
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showRevisionModal, setShowRevisionModal] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState<any>(null);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [approvalLanguage, setApprovalLanguage] = useState('en');
  const [approvalSubject, setApprovalSubject] = useState('');
  const [approvalBody, setApprovalBody] = useState('');
  const [approvalCopySuccess, setApprovalCopySuccess] = useState(false);
  const [approvalLanguageOpen, setApprovalLanguageOpen] = useState(false);
  const [approvalSuccess, setApprovalSuccess] = useState(false);
  const [revisionLanguage, setRevisionLanguage] = useState('en');
  const [revisionSubject, setRevisionSubject] = useState('');
  const [revisionBody, setRevisionBody] = useState('');
  const [revisionCopySuccess, setRevisionCopySuccess] = useState(false);
  const [revisionLanguageOpen, setRevisionLanguageOpen] = useState(false);
  const [revisionSuccess, setRevisionSuccess] = useState(false);
  
  // Category info modal state
  const [showCategoryInfo, setShowCategoryInfo] = useState<string | null>(null);
  
  
  // Briefing requirements modal state
  const [showBriefingModal, setShowBriefingModal] = useState(false);

  // Defer AG Grid mount until the browser has laid out its container div.
  const [gridMountReady, setGridMountReady] = useState(false);
  const gridDeferRafIds = useRef<number[]>([]);
  const gridApiRef = useRef<any>(null);
  const gridContainerRef = useCallback((node: HTMLDivElement | null) => {
    gridDeferRafIds.current.forEach(id => cancelAnimationFrame(id));
    gridDeferRafIds.current = [];
    if (node) {
      setGridMountReady(false);
      const raf1 = requestAnimationFrame(() => {
        const raf2 = requestAnimationFrame(() => {
          setGridMountReady(true);
        });
        gridDeferRafIds.current.push(raf2);
      });
      gridDeferRafIds.current.push(raf1);
    } else {
      setGridMountReady(false);
      gridApiRef.current = null;
    }
  }, []);
  const onGridReady = useCallback((params: any) => {
    gridApiRef.current = params.api;
  }, []);

  // Re-fit columns only when sidebar toggles (after the 300ms CSS transition)
  useEffect(() => {
    if (!gridApiRef.current) return;
    const timer = setTimeout(() => {
      gridApiRef.current?.sizeColumnsToFit();
    }, 350);
    return () => clearTimeout(timer);
  }, [sidebarOpen]);

  const handleTrialWelcomeDismiss = (startProductTour: boolean) => {
    setShowTrialWelcome(false);
    markIntroTourShown(); // prevent the generic tour from firing after
    if (startProductTour) {
      setTimeout(() => {
        const tour = createBrandCampaignTour(
          () => setSidebarOpen(true),
          null,
          null,
          false,
          false // no intro step — we already showed the trial modal
        );
        tour.start();
      }, 300);
    }
  };

  // Tour function - context-aware based on current step
  const startTour = (isAutoStart: boolean = false) => {
    const tour = createBrandCampaignTour(
      () => setSidebarOpen(true),
      selectedCampaignUuid ? null : step, // Only pass step if not viewing a campaign
      selectedCampaignUuid,
      !!selectedFile || !!pastedText.trim(), // hasFile = true if file or text is provided
      isAutoStart // Pass auto-start flag to show intro step
    );
    tour.start();
  };

  // Trial welcome modal — separate from the product tour, keyed per-user
  useEffect(() => {
    if (subLoading) return;
    if (subscription?.status !== 'trialing') return;
    const key = `brandcomply_trial_welcome_${subscription?.plan_id || 'shown'}`;
    if (localStorage.getItem(key) === 'true') return;
    localStorage.setItem(key, 'true');
    setShowTrialWelcome(true);
  }, [subLoading]);

  // Auto-start product tour for new users (only on first login)
  useEffect(() => {
    if (subLoading) return;
    if (showTrialWelcome) return; // trial modal will trigger tour on dismiss
    if (hasSeenIntroTour()) return;
    markIntroTourShown();
    const timer = setTimeout(() => {
      const tour = createBrandCampaignTour(
        () => setSidebarOpen(true),
        null,
        null,
        false,
        true
      );
      tour.start();
    }, 1000);
    return () => clearTimeout(timer);
  }, [subLoading, showTrialWelcome]);

  // Fetch campaign data when selected
  useEffect(() => {
    if (selectedCampaignUuid) {
      fetchCampaignData(selectedCampaignUuid);
    } else {
      setSelectedCampaign(null);
    }
  }, [selectedCampaignUuid]);

  const fetchCampaignData = async (campaignUuid: string) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${API_ROOT}/brand/campaigns`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const campaign = data.campaigns?.find((c: any) => c.campaign_uuid === campaignUuid);
        if (campaign) {
          setSelectedCampaign(campaign);
        }
      }
    } catch (err) {
      console.error('Error fetching campaign data:', err);
    }
  };

  const handleCampaignSelect = (campaignUuid: string) => {
    if (campaignUuid) {
      setSelectedCampaignUuid(campaignUuid);
      setStep('upload'); // Reset to upload step when viewing submissions
    } else {
      setSelectedCampaignUuid(null);
      setSelectedCampaign(null);
    }
  };

  // Fetch user info
  useEffect(() => {
    const fetchUserInfo = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      try {
        const response = await fetch(`${API_ROOT}/users/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setUserInfo(data);
        }
      } catch (err) {
        console.error('Failed to fetch user info:', err);
      }
    };
    fetchUserInfo();
  }, []);

  // Timer-based progress for visual feedback during processing (90 seconds)
  useEffect(() => {
    if (!isProcessing) {
      setTimerProgress(0);
      return;
    }

    const startTime = Date.now();
    const duration = 15000; // 90 seconds

    const intervalId = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const percentage = Math.min(100, Math.round((elapsed / duration) * 100));
      setTimerProgress(percentage);

      if (percentage >= 100) {
        clearInterval(intervalId);
      }
    }, 100); // Update every 100ms

    return () => clearInterval(intervalId);
  }, [isProcessing]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('userType');
    localStorage.removeItem('brandcomply_intro_shown');
    localStorage.removeItem('brandcomply_tour_completed');
    sessionStorage.removeItem('trial_banner_dismissed');
    navigate('/');
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const validTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/plain'
      ];
      
      if (validTypes.includes(file.type)) {
        setSelectedFile(file);
        setError(null);
      } else {
        setError('Please upload a PDF, Word document, or text file');
        setSelectedFile(null);
      }
    }
  };

  const handleExtractFromFile = async () => {
    if (!selectedFile) return;

    setIsProcessing(true);
    setError(null);
    setProgress(0);
    setProgressStage('Preparing document...');

    const progressInterval = setInterval(() => {
      setProgress((prev) => Math.min(prev + 2, 95));
    }, 200);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_ROOT}/extract-briefing`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });

      clearInterval(progressInterval);
      setProgress(100);

      if (!response.ok) {
        throw new Error('Failed to extract briefing data');
      }

      const data = await response.json();
      processExtractedData(data);
    } catch (err) {
      clearInterval(progressInterval);
      setError(err instanceof Error ? err.message : 'Failed to extract briefing');
    } finally {
      setIsProcessing(false);
      setProgress(0);
      setProgressStage('');
    }
  };

  const handleExtractFromText = async () => {
    if (!pastedText.trim()) {
      setError('Please paste some text');
      return;
    }

    setIsProcessing(true);
    setError(null);
    setProgress(0);
    setProgressStage('Analyzing pasted text...');

    const progressInterval = setInterval(() => {
      setProgress((prev) => Math.min(prev + 3, 95));
    }, 200);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_ROOT}/extract-briefing-text`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ text: pastedText }),
      });

      clearInterval(progressInterval);
      setProgress(100);

      if (!response.ok) {
        throw new Error('Failed to extract briefing data');
      }

      const data = await response.json();
      processExtractedData(data);
    } catch (err) {
      clearInterval(progressInterval);
      setError(err instanceof Error ? err.message : 'Failed to extract briefing');
    } finally {
      setIsProcessing(false);
      setProgress(0);
      setProgressStage('');
    }
  };

  const processExtractedData = (data: ExtractedBriefingData) => {
    setExtractedData(data);
    setStep('review');
  };

  const handlePublishCampaign = async () => {
    if (!extractedData) return;
    
    if (!extractedData.campaign_name?.trim()) {
      setError('Campaign name is required');
      return;
    }

    // Create structured requirements object with categories
    const structuredRequirements: any = {};
    
    if (extractedData.hooks?.filter(h => h.trim()).length) {
      structuredRequirements.hooks = extractedData.hooks.filter(h => h.trim());
    }
    if (extractedData.topics?.filter(t => t.trim()).length) {
      structuredRequirements.topics = extractedData.topics.filter(t => t.trim());
    }
    if (extractedData.call_to_actions?.filter(c => c.trim()).length) {
      structuredRequirements.call_to_actions = extractedData.call_to_actions.filter(c => c.trim());
    }
    if (extractedData.elements?.filter(e => e.trim()).length) {
      structuredRequirements.elements = extractedData.elements.filter(e => e.trim());
    }
    if (extractedData.story?.trim()) {
      structuredRequirements.story = extractedData.story.trim();
    }
    if (extractedData.visuals_background?.trim()) {
      structuredRequirements.visuals_background = extractedData.visuals_background.trim();
    }
    
    // Also create a flat list for backward compatibility (used for video analysis)
    const allRequirements: string[] = [];
    if (structuredRequirements.hooks) allRequirements.push(...structuredRequirements.hooks);
    if (structuredRequirements.topics) allRequirements.push(...structuredRequirements.topics);
    if (structuredRequirements.call_to_actions) allRequirements.push(...structuredRequirements.call_to_actions);
    if (structuredRequirements.elements) allRequirements.push(...structuredRequirements.elements);
    if (structuredRequirements.story) allRequirements.push(structuredRequirements.story);
    if (structuredRequirements.visuals_background) allRequirements.push(structuredRequirements.visuals_background);
    
    if (allRequirements.length === 0) {
      setError('At least one requirement is needed');
      return;
    }

    setIsPublishing(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Not authenticated');
      }
      
      const response = await fetch(`${API_ROOT}/brand/campaigns`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          campaign_name: extractedData.campaign_name,
          brand_name: extractedData.brand_name,
          product_name: extractedData.product_name,
          requirements: allRequirements, // Flat list for video analysis
          requirements_structured: structuredRequirements, // Structured for display
          brand_logo_path: extractedData.brand_logo_path || null,
          product_image_path: extractedData.product_image_path || null,
        }),
      });

      if (!response.ok) {
        let errorMessage = 'Failed to create campaign';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorData.message || errorMessage;
          
          // If it's a 403, provide more helpful message
          if (response.status === 403) {
            if (errorMessage.includes('user type')) {
              errorMessage = `Access denied: ${errorMessage}. Please contact support to update your account type, or try logging out and registering again as a brand user.`;
            } else {
              errorMessage = `Access denied: ${errorMessage}. You may need to update your account type to 'brand'.`;
            }
          }
        } catch (e) {
          // If JSON parsing fails, use status text
          errorMessage = `Failed to create campaign (${response.status}): ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      const link = `${window.location.origin}/upload/${data.campaign_uuid}`;
      setCampaignLink(link);
      setStep('published');
      // Trigger sidebar refresh to show new campaign
      setSidebarRefreshTrigger(prev => prev + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to publish campaign');
    } finally {
      setIsPublishing(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(campaignLink);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const handleStartNew = () => {
    setStep('upload');
    setSelectedFile(null);
    setPastedText('');
    setExtractedData(null);
    setCampaignLink('');
    setError(null);
    setSelectedCampaignUuid(null);
  };

  const handleApprovalClick = useCallback((submission: any) => {
    setSelectedSubmission(submission);
    setShowApprovalModal(true);
    setApprovalSuccess(false);
  }, []);

  const handleRevisionClick = useCallback((submission: any) => {
    setSelectedSubmission(submission);
    setShowRevisionModal(true);
    setRevisionSuccess(false);
  }, []);

  const buildApprovalEmail = (language: string) => {
    const creatorName = selectedSubmission?.creator_first_name || selectedSubmission?.creator_name || 'Creator';
    const campaignName = selectedCampaign?.campaign_name || 'your campaign';
    const complianceScore = Math.round(selectedSubmission?.compliance_score || 0);
    const submissionUrl = `${window.location.origin}/campaign-result/${selectedSubmission?.analysis_id}`;
    const companyName = userInfo?.company_name || 'The Brand';
    const signature = `The ${companyName} Team`;

    const templates: Record<string, { subject: string; body: string }> = {
      en: {
        subject: '✅ Your Video Submission has been Approved!',
        body: `Dear ${creatorName},\n\nGreat news! Your video submission for ${campaignName} has been approved.\n\nYou can view your approved submission here:\n${submissionUrl}\n\nThank you for your excellent work!\n\nBest regards,\n${signature}`,
      },
      de: {
        subject: '✅ Deine Videoeinreichung wurde genehmigt!',
        body: `Hallo ${creatorName},\n\nGute Nachrichten! Deine Videoeinreichung für ${campaignName} wurde genehmigt.\n\nHier kannst du deine genehmigte Einreichung ansehen:\n${submissionUrl}\n\nVielen Dank für deine grossartige Arbeit!\n\nBeste Grüsse,\n${signature}`,
      },
      fr: {
        subject: '✅ Votre soumission vidéo a été approuvée !',
        body: `Bonjour ${creatorName},\n\nBonne nouvelle ! Votre soumission vidéo pour ${campaignName} a été approuvée.\n\nVous pouvez consulter votre soumission approuvée ici :\n${submissionUrl}\n\nMerci pour votre excellent travail !\n\nCordialement,\n${signature}`,
      },
      es: {
        subject: '✅ Tu envío de video ha sido aprobado',
        body: `Hola ${creatorName},\n\n¡Buenas noticias! Tu envío de video para ${campaignName} ha sido aprobado.\n\nPuedes ver tu envío aprobado aquí:\n${submissionUrl}\n\n¡Gracias por tu excelente trabajo!\n\nSaludos cordiales,\n${signature}`,
      },
      pt: {
        subject: '✅ Sua submissão de vídeo foi aprovada',
        body: `Olá ${creatorName},\n\nBoas notícias! Sua submissão de vídeo para ${campaignName} foi aprovada.\n\nVocê pode ver sua submissão aprovada aqui:\n${submissionUrl}\n\nObrigado pelo excelente trabalho!\n\nAtenciosamente,\n${signature}`,
      },
    };

    return templates[language] || templates.en;
  };

  const buildRevisionEmail = (language: string) => {
    const creatorName = selectedSubmission?.creator_first_name || selectedSubmission?.creator_name || 'Creator';
    const campaignName = selectedCampaign?.campaign_name || 'your campaign';
    const complianceScore = Math.round(selectedSubmission?.compliance_score || 0);
    const submissionUrl = `${window.location.origin}/campaign-result/${selectedSubmission?.analysis_id}`;
    const campaignUploadUrl = `${window.location.origin}/upload/${selectedCampaign?.campaign_uuid || selectedCampaignUuid}`;
    const companyName = userInfo?.company_name || 'The Brand';
    const signature = `The ${companyName} Team`;

    const templates: Record<string, { subject: string; body: string }> = {
      en: {
        subject: '🔄 Revision Requested for Your Video Submission',
        body: `Dear ${creatorName},\n\nThank you for your submission for ${campaignName}.\n\nWe would like to request some revisions to better align with our campaign briefing:\n\n>>> PLEASE ADD YOUR SPECIFIC REVISION FEEDBACK HERE <<<\n\nYou can view your submission here:\n${submissionUrl}\n\nTo upload a revised version, please use this link:\n${campaignUploadUrl}\n\nWe look forward to seeing your revised submission!\n\nBest regards,\n${signature}`,
      },
      de: {
        subject: '🔄 Überarbeitung für deine Videoeinreichung erforderlich',
        body: `Hallo ${creatorName},\n\nDanke für deine Einreichung für ${campaignName}.\n\nWir möchten einige Überarbeitungen anfragen, damit das Video besser mit dem Briefing übereinstimmt:\n\n>>> BITTE HIER KONKRETES FEEDBACK ERGÄNZEN <<<\n\nDu kannst deine Einreichung hier ansehen:\n${submissionUrl}\n\nUm eine überarbeitete Version hochzuladen, verwende bitte diesen Link:\n${campaignUploadUrl}\n\nWir freuen uns auf deine überarbeitete Einreichung!\n\nBeste Grüsse,\n${signature}`,
      },
      fr: {
        subject: '🔄 Révision demandée pour votre soumission vidéo',
        body: `Bonjour ${creatorName},\n\nMerci pour votre soumission pour ${campaignName}.\n\nNous souhaitons demander quelques révisions afin de mieux respecter le briefing de campagne :\n\n>>> VEUILLEZ AJOUTER ICI UN RETOUR PRÉCIS <<<\n\nVous pouvez consulter votre soumission ici :\n${submissionUrl}\n\nPour envoyer une version révisée, veuillez utiliser ce lien :\n${campaignUploadUrl}\n\nNous avons hâte de recevoir votre version révisée !\n\nCordialement,\n${signature}`,
      },
      es: {
        subject: '🔄 Revisión solicitada para tu envío de video',
        body: `Hola ${creatorName},\n\nGracias por tu envío para ${campaignName}.\n\nNos gustaría solicitar algunas revisiones para alinearlo mejor con el briefing de la campaña:\n\n>>> AGREGA AQUÍ TUS COMENTARIOS ESPECÍFICOS <<<\n\nPuedes ver tu envío aquí:\n${submissionUrl}\n\nPara subir una versión revisada, utiliza este enlace:\n${campaignUploadUrl}\n\n¡Esperamos tu versión revisada!\n\nSaludos cordiales,\n${signature}`,
      },
      pt: {
        subject: '🔄 Revisão solicitada para sua submissão de vídeo',
        body: `Olá ${creatorName},\n\nObrigado pela sua submissão para ${campaignName}.\n\nGostaríamos de solicitar algumas revisões para alinhar melhor com o briefing da campanha:\n\n>>> ADICIONE AQUI SEU FEEDBACK ESPECÍFICO <<<\n\nVocê pode ver sua submissão aqui:\n${submissionUrl}\n\nPara enviar uma versão revisada, use este link:\n${campaignUploadUrl}\n\nAguardamos sua versão revisada!\n\nAtenciosamente,\n${signature}`,
      },
    };

    return templates[language] || templates.en;
  };

  useEffect(() => {
    if (!showApprovalModal || !selectedSubmission) return;
    const template = buildApprovalEmail(approvalLanguage);
    setApprovalSubject(template.subject);
    setApprovalBody(template.body);
    setApprovalCopySuccess(false);
    setApprovalSuccess(false);
  }, [showApprovalModal, approvalLanguage, selectedSubmission, selectedCampaign, userInfo]);

  useEffect(() => {
    if (!showRevisionModal || !selectedSubmission) return;
    const template = buildRevisionEmail(revisionLanguage);
    setRevisionSubject(template.subject);
    setRevisionBody(template.body);
    setRevisionCopySuccess(false);
    setRevisionSuccess(false);
  }, [showRevisionModal, revisionLanguage, selectedSubmission, selectedCampaign, userInfo]);

  const visibleSubmissions = useMemo(
    () =>
      (selectedCampaign?.submissions || []).filter(
        (submission: any) => submission?.submission_status !== 'discarded'
      ),
    [selectedCampaign?.submissions]
  );

  const gridColumnDefs = useMemo<any[]>(() => [
                        {
                          headerName: 'Creator',
                          headerTooltip: 'The name of the content creator who submitted the video',
                          field: 'creator_name',
                          width: 200,
                          minWidth: 110,
                          resizable: false,
                          filter: false,
                          suppressMovable: true,
                          tooltipField: 'creator_name',
                          valueGetter: (params: any) => {
                            const f = params.data?.creator_first_name || '';
                            const s = params.data?.creator_surname || '';
                            return (f && s ? `${f} ${s}`.trim() : params.data?.creator_name || 'Unknown').toLowerCase();
                          },
                          cellStyle: { 
                            lineHeight: '1.4', 
                            paddingTop: '8px', 
                            paddingBottom: '8px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color: PALETTE.gunmetal
                          },
                          cellRenderer: (params: ICellRendererParams) => {
                            const firstName = params.data?.creator_first_name || '';
                            const surname = params.data?.creator_surname || '';
                            const fullName = firstName && surname 
                              ? `${firstName} ${surname}`.trim()
                              : params.data?.creator_name || 'Unknown';
                            return (
                              <div 
                                className="w-full truncate"
                                style={{ 
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  color: PALETTE.gunmetal
                                }}
                              >
                                {fullName}
                              </div>
                            );
                          },
                          tooltipValueGetter: (params: any) => {
                            const firstName = params.data?.creator_first_name || '';
                            const surname = params.data?.creator_surname || '';
                            const fullName = firstName && surname 
                              ? `${firstName} ${surname}`.trim()
                              : params.data?.creator_name || 'Unknown';
                            return fullName;
                          }
                        },
                        {
                          headerName: 'Status',
                          headerTooltip: 'Current status: Submitted (awaiting review), Approved (ready to go), or In Revision (needs changes)',
                          field: 'submission_status',
                          width: 110,
                          minWidth: 80,
                          resizable: false,
                          filter: false,
                          suppressMovable: true,
                          valueGetter: (params: any) => {
                            const approval = params.data?.approval_status;
                            if (approval === 'approved') return 3;
                            if (approval === 'revision_requested') return 2;
                            if (params.data?.submission_status === 'submitted') return 1;
                            return 0;
                          },
                          sort: 'asc' as const,
                          cellStyle: {
                            lineHeight: '1.4',
                            paddingTop: '8px',
                            paddingBottom: '8px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color: PALETTE.gunmetal,
                            fontSize: '0.875rem',
                          },
                          cellRenderer: (params: ICellRendererParams) => {
                            const approvalStatus = params.data?.approval_status;
                            if (approvalStatus === 'approved') {
                              return (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                  <CheckCircle size={15} style={{ color: PALETTE.inchworm, flexShrink: 0 }} />
                                  Approved
                                </span>
                              );
                            }
                            if (approvalStatus === 'revision_requested') {
                              return (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                  <RefreshCcw size={14} style={{ color: PALETTE.orange, flexShrink: 0 }} />
                                  In Revision
                                </span>
                              );
                            }
                            const status = params.data?.submission_status || 'pending';
                            return status.charAt(0).toUpperCase() + status.slice(1);
                          },
                          tooltipValueGetter: (params: any) => {
                            const approvalStatus = params.data?.approval_status;
                            const submissionStatus = params.data?.submission_status;
                            if (approvalStatus === 'approved') {
                              return 'This submission has been approved by you and is ready to go';
                            }
                            if (approvalStatus === 'revision_requested') {
                              return 'This submission needs revisions - creator has been notified';
                            }
                            if (submissionStatus === 'pending') {
                              return 'The creator has checked the video but not yet submitted';
                            }
                            return 'Submitted — awaiting your review';
                          }
                        },
                        {
                          headerName: 'Timestamp',
                          headerTooltip: 'Date and time when the video was submitted or created',
                          field: 'submitted_at',
                          width: 170,
                          minWidth: 140,
                          resizable: false,
                          filter: false,
                          suppressMovable: true,
                          valueGetter: (params: any) => {
                            const status = params.data?.submission_status;
                            const submittedAt = params.data?.submitted_at;
                            const createdAt = params.data?.created_at;
                            const ts = (status === 'submitted' && submittedAt) ? submittedAt : createdAt;
                            if (!ts) return 0;
                            if (typeof ts === 'string' && !ts.includes('Z') && !ts.includes('+')) {
                              return new Date(ts.replace(' ', 'T') + 'Z').getTime();
                            }
                            return new Date(ts).getTime();
                          },
                          sort: 'desc' as const,
                          cellStyle: {
                            lineHeight: '1.4', 
                            paddingTop: '8px', 
                            paddingBottom: '8px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color: PALETTE.gunmetal
                          },
                          cellRenderer: (params: ICellRendererParams) => {
                            // Use submitted_at if available (submitted status), otherwise use created_at
                            const submissionStatus = params.data?.submission_status;
                            const submittedAt = params.data?.submitted_at;
                            const createdAt = params.data?.created_at;
                            const timestamp = (submissionStatus === 'submitted' && submittedAt) ? submittedAt : createdAt;
                            
                            if (!timestamp) return <span style={{ color: PALETTE.americanSilver }}>-</span>;
                            
                            // Parse UTC timestamp from backend and convert to local time
                            // SQLite returns timestamps like "YYYY-MM-DD HH:MM:SS" in UTC
                            // We need to explicitly treat it as UTC before converting to local
                            let date: Date;
                            if (typeof timestamp === 'string' && !timestamp.includes('Z') && !timestamp.includes('+')) {
                              // Add 'Z' to indicate UTC if not present
                              const utcTimestamp = timestamp.replace(' ', 'T') + 'Z';
                              date = new Date(utcTimestamp);
                            } else {
                              date = new Date(timestamp);
                            }
                            
                            const formatted = date.toLocaleString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            });
                            return (
                              <div 
                                className="w-full truncate"
                                style={{ 
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  color: PALETTE.gunmetal,
                                  fontSize: '0.875rem'
                                }}
                              >
                                {formatted}
                              </div>
                            );
                          },
                          tooltipValueGetter: (params: any) => {
                            const submissionStatus = params.data?.submission_status;
                            const submittedAt = params.data?.submitted_at;
                            const createdAt = params.data?.created_at;
                            const timestamp = (submissionStatus === 'submitted' && submittedAt) ? submittedAt : createdAt;
                            
                            if (!timestamp) return '';
                            
                            // Parse UTC timestamp and convert to local time
                            let date: Date;
                            if (typeof timestamp === 'string' && !timestamp.includes('Z') && !timestamp.includes('+')) {
                              const utcTimestamp = timestamp.replace(' ', 'T') + 'Z';
                              date = new Date(utcTimestamp);
                            } else {
                              date = new Date(timestamp);
                            }
                            
                            const label = (submissionStatus === 'submitted' && submittedAt) ? 'Submitted: ' : 'Created: ';
                            return label + date.toLocaleString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              second: '2-digit'
                            });
                          }
                        },
                        {
                          headerName: 'Version',
                          headerTooltip: 'Submission version number (V1, V2, etc.) - increases when creator resubmits',
                          field: 'version',
                          width: 90,
                          minWidth: 70,
                          resizable: false,
                          filter: false,
                          suppressMovable: true,
                          comparator: (a: any, b: any, nodeA: any, nodeB: any, isDesc: boolean) => {
                            const aSubmitted = nodeA?.data?.submission_status === 'submitted';
                            const bSubmitted = nodeB?.data?.submission_status === 'submitted';
                            const aVal = aSubmitted ? (a || 1) : (isDesc ? -Infinity : Infinity);
                            const bVal = bSubmitted ? (b || 1) : (isDesc ? -Infinity : Infinity);
                            return aVal - bVal;
                          },
                          cellStyle: { 
                            lineHeight: '1.4', 
                            paddingTop: '8px', 
                            paddingBottom: '8px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontSize: '0.875rem',
                            textAlign: 'center'
                          },
                          cellRenderer: (params: ICellRendererParams) => {
                            const status = params.data?.submission_status;
                            // Only show version if submission is submitted
                            if (status !== 'submitted') {
                              return <span style={{ color: PALETTE.americanSilver }}>-</span>;
                            }
                            const version = params.value || 1;
                            return (
                              <span style={{ color: PALETTE.gunmetal }}>
                                V{version}
                              </span>
                            );
                          },
                          tooltipValueGetter: (params: any) => {
                            const version = params.value || 1;
                            return `Submission version ${version}`;
                          }
                        },
                        {
                          headerName: 'Score',
                          headerTooltip: 'The BrandComply score shows what percentage of the campaign brief the video aligns with.',
                          field: 'compliance_score',
                          width: 90,
                          minWidth: 70,
                          resizable: false,
                          filter: false,
                          suppressMovable: true,
                          comparator: (a: any, b: any, _nodeA: any, _nodeB: any, isDesc: boolean) => {
                            const sentinel = isDesc ? -Infinity : Infinity;
                            const aVal = (a === null || a === undefined) ? sentinel : a;
                            const bVal = (b === null || b === undefined) ? sentinel : b;
                            return aVal - bVal;
                          },
                          cellStyle: { 
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '8px',
                            overflow: 'visible',
                            whiteSpace: 'nowrap'
                          },
                          cellRenderer: (params: ICellRendererParams) => {
                            const score = params.value;
                            if (score === null || score === undefined) {
                              return <span style={{ color: PALETTE.americanSilver }}>N/A</span>;
                            }
                            const scoreNum = Math.round(score);
                            // Color coding: green for 90-100%, yellow for 61-89%, red for 0-60%
                            // Text color is gunmetal for better readability
                            const bgColor = scoreNum >= 90 
                              ? PALETTE.inchworm 
                              : scoreNum >= 61 
                                ? '#fbbf24' 
                                : '#fca5a5';
                            return (
                              <span 
                                className="px-3 py-1.5 rounded text-sm font-bold whitespace-nowrap"
                                style={{ 
                                  color: PALETTE.gunmetal,
                                  backgroundColor: bgColor
                                }}
                              >
                                {scoreNum}%
                              </span>
                            );
                          },
                          tooltipValueGetter: (params: any) => {
                            const score = params.value;
                            if (score === null || score === undefined) return 'No score available';
                            const scoreNum = Math.round(score);
                            if (scoreNum === 100) return `${scoreNum}% - Excellent! Video meets all requirements`;                            
                            if (scoreNum >= 61) return `${scoreNum}% - Good! Most requirements met, minor improvements needed`;
                            return `${scoreNum}% - Needs work. Several requirements missing`;
                          }
                        },
                        {
                          headerName: 'Results',
                          headerTooltip: 'Click to view the full analysis with requirement-by-requirement breakdown',
                          field: 'analysis_id',
                          width: 90,
                          minWidth: 70,
                          resizable: false,
                          filter: false,
                          suppressMovable: true,
                          sortable: false,
                          cellStyle: { 
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '8px',
                            overflow: 'visible',
                            whiteSpace: 'nowrap'
                          },
                          headerClass: 'ag-center-aligned-header',
                          cellRenderer: (params: ICellRendererParams) => {
                            const analysisId = params.value;
                            const submissionStatus = params.data?.submission_status;
                            
                            // Only show view button for submitted videos
                            if (!analysisId || submissionStatus !== 'submitted') {
                              return <span style={{ color: PALETTE.americanSilver }}>-</span>;
                            }
                            
                            // Use the correct route: /campaign-result/:unique_id (not /campaign-analysis/:unique_id)
                            // Include campaign_uuid as query param for proper context
                            const submissionUrl = `/campaign-result/${analysisId}${selectedCampaignUuid ? `?campaign=${selectedCampaignUuid}` : ''}`;
                            return (
                              <a
                                href={submissionUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-1 text-sm hover:underline whitespace-nowrap"
                                style={{ color: PALETTE.gunmetal }}
                              >
                                View
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            );
                          },
                          tooltipValueGetter: (params: any) => {
                            if (!params.value) return 'No analysis available';
                            return 'Click to open detailed analysis in new tab';
                          }
                        },
                        {
                          headerName: 'Approval',
                          headerTooltip: 'Approve this submission and notify the creator',
                          tooltipValueGetter: () => 'Approve this submission',
                          field: 'approval',
                          width: 90,
                          minWidth: 80,
                          resizable: false,
                          filter: false,
                          suppressMovable: true,
                          sortable: false,
                          headerClass: 'approval-column-header',
                          cellClass: 'approval-column-cell',
                          cellStyle: { 
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '8px'
                          },
                          cellRenderer: (params: ICellRendererParams) => {
                            const approvalStatus = params.data?.approval_status;
                            const submissionStatus = params.data?.submission_status;
                            
                            // Only show button for submitted submissions (not pending or discarded)
                            if (submissionStatus !== 'submitted') {
                              return null;
                            }
                            
                            // If already approved or revision requested, don't show any button
                            if (approvalStatus === 'approved' || approvalStatus === 'revision_requested') {
                              return null;
                            }
                            
                            return (
                              <button
                                onClick={() => handleApprovalClick(params.data)}
                                className="flex items-center justify-center p-2 rounded transition-colors"
                                style={{
                                  background: PALETTE.americanSilver,
                                  color: PALETTE.gunmetal,
                                  border: 'none',
                                  cursor: 'pointer'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.opacity = '0.8';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.opacity = '1';
                                }}
                              >
                                <ThumbsUp className="w-4 h-4" />
                              </button>
                            );
                          }
                        },
                        {
                          headerName: 'Revision',
                          headerTooltip: 'Request revisions from the creator with specific feedback on what to improve',
                          tooltipValueGetter: () => 'Request revisions from the creator',
                          field: 'revision',
                          width: 90,
                          minWidth: 80,
                          resizable: false,
                          filter: false,
                          suppressMovable: true,
                          sortable: false,
                          headerClass: 'revision-column-header',
                          cellClass: 'revision-column-cell',
                          cellStyle: { 
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '8px'
                          },
                          cellRenderer: (params: ICellRendererParams) => {
                            const approvalStatus = params.data?.approval_status;
                            const submissionStatus = params.data?.submission_status;
                            
                            // Only show button for submitted submissions (not pending or discarded)
                            if (submissionStatus !== 'submitted') {
                              return null;
                            }
                            
                            // If already approved or revision requested, don't show any button
                            if (approvalStatus === 'approved' || approvalStatus === 'revision_requested') {
                              return null;
                            }
                            
                            return (
                              <button
                                onClick={() => handleRevisionClick(params.data)}
                                className="flex items-center justify-center p-2 rounded transition-colors"
                                style={{
                                  background: PALETTE.americanSilver,
                                  color: PALETTE.gunmetal,
                                  border: 'none',
                                  cursor: 'pointer'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.opacity = '0.8';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.opacity = '1';
                                }}
                              >
                                <RefreshCcw className="w-4 h-4" />
                              </button>
                            );
                          }
                        },
                        {
                          headerName: 'Download',
                          headerTooltip: 'Download the submitted video file',
                          tooltipValueGetter: () => 'Download video',
                          field: 'download',
                          width: 90,
                          minWidth: 80,
                          resizable: false,
                          filter: false,
                          suppressMovable: true,
                          sortable: false,
                          cellStyle: { 
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '8px'
                          },
                          cellRenderer: (params: ICellRendererParams) => {
                            const analysisId = params.data?.analysis_id;
                            const submissionStatus = params.data?.submission_status;
                            
                            // Only show download for submitted videos
                            if (!analysisId || submissionStatus !== 'submitted') {
                              return <span style={{ color: PALETTE.americanSilver }}>-</span>;
                            }
                            
                            const handleDownload = async () => {
                              try {
                                const token = localStorage.getItem('token');
                                const response = await fetch(`${API_ROOT}/video/${analysisId}/url`, {
                                  headers: {
                                    'Authorization': `Bearer ${token}`
                                  }
                                });
                                
                                if (!response.ok) {
                                  throw new Error('Failed to get download URL');
                                }
                                
                                const data = await response.json();
                                if (data.presigned_url) {
                                  const blobRes = await fetch(data.presigned_url);
                                  if (!blobRes.ok) throw new Error('Failed to fetch video');
                                  const blob = await blobRes.blob();
                                  const filename = data.presigned_url.split('/').pop()?.split('?')[0] || `video_${analysisId}.mp4`;
                                  const url = URL.createObjectURL(blob);
                                  const a = document.createElement('a');
                                  a.href = url;
                                  a.download = filename;
                                  document.body.appendChild(a);
                                  a.click();
                                  document.body.removeChild(a);
                                  URL.revokeObjectURL(url);
                                }
                              } catch (error) {
                                console.error('Download error:', error);
                                alert('Failed to download video. Please try again.');
                              }
                            };
                            
                            return (
                              <button
                                onClick={handleDownload}
                                className="flex items-center justify-center p-1.5 rounded transition-colors"
                                style={{
                                  background: 'transparent',
                                  color: PALETTE.gunmetal,
                                  border: 'none',
                                  cursor: 'pointer'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = `${PALETTE.americanSilver}30`;
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = 'transparent';
                                }}
                              >
                                <Download className="w-4 h-4" />
                              </button>
                            );
                          }
                        }
  ], [selectedCampaignUuid, handleApprovalClick, handleRevisionClick]);

  return (
    <div className="flex h-screen overflow-hidden">
      <SEO
        title="Campaign Manager"
        description="Manage your influencer and UGC campaigns. Create briefs, track creator submissions, and review video compliance."
        path="/brand/campaign"
      />
      {/* Campaigns Sidebar */}
      <BrandCampaignsSidebar
        collapsed={!sidebarOpen}
        onToggleSidebar={() => setSidebarOpen((prev) => !prev)}
        onCampaignSelect={handleCampaignSelect}
        selectedCampaignUuid={selectedCampaignUuid}
        onNewCampaignClick={handleStartNew}
        refreshTrigger={sidebarRefreshTrigger}
        onStartTour={() => startTour(false)}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden relative" style={{ background: `linear-gradient(to bottom right, ${PALETTE.brightSnow}, ${PALETTE.americanSilver}20)` }}>
        {/* Feature Request & Help/Tour Buttons - Top Right */}
        <div className="absolute top-4 right-8 z-10 flex items-center gap-3">
          <FeatureRequestButton />
          <button
            onClick={() => startTour(false)}
            aria-label="Help / Tour"
            className="tour-help-btn bg-white border border-gray-300 shadow-md rounded-full w-10 h-10 flex items-center justify-center hover:bg-gray-100 transition"
            style={{ boxShadow: '0 2px 8px rgba(36,56,55,0.10)' }}
          >
            <HelpCircle size={20} color={PALETTE.gunmetal} />
          </button>
        </div>

        {/* Tour styles */}
        <style>{tourStyles}</style>
        
        {/* Main Content - Scrollable */}
        <main className="flex-1 overflow-y-auto">
          {subscription?.status === 'trialing' && !trialBannerDismissed && (
            <div className="ml-6 mt-4 mb-0 px-5 py-3 rounded-xl flex items-center justify-between" style={{ background: `${PALETTE.inchworm}12`, border: `1px solid ${PALETTE.inchworm}40`, marginRight: 240 }}>
              <div className="flex items-center gap-3">
                <Zap className="w-4 h-4 flex-shrink-0" style={{ color: PALETTE.inchworm }} />
                <span className="text-sm" style={{ color: PALETTE.gunmetal }}>
                  <strong>14-day free trial</strong> — {subscription?.trial_days_remaining ?? 0} days & {subscription?.remaining ?? 30}/{subscription?.monthly_limit ?? 30} checks left
                </span>
              </div>
              <button
                onClick={() => { setTrialBannerDismissed(true); sessionStorage.setItem('trial_banner_dismissed', 'true'); }}
                className="p-1 rounded hover:bg-black/5 transition-colors flex-shrink-0 ml-3"
              >
                <X className="w-3.5 h-3.5" style={{ color: `${PALETTE.gunmetal}60` }} />
              </button>
            </div>
          )}
          {selectedCampaignUuid && selectedCampaign ? (
            /* Submissions Table View */
            <div className="max-w-7xl mx-auto px-6 py-12">
              <div className="mb-6">
                <h1 className="text-2xl font-bold mb-2" style={{ color: PALETTE.gunmetal }}>
                  {selectedCampaign.campaign_name}
                </h1>
                <p className="text-sm" style={{ color: `${PALETTE.gunmetal}70` }}>
                  {selectedCampaign.brand_name} {selectedCampaign.product_name && `• ${selectedCampaign.product_name}`}
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                {/* Creator Upload Link Button - Important/Primary */}
                <Button
                  onClick={() => {
                    const link = `${window.location.origin}/upload/${selectedCampaignUuid}`;
                    navigator.clipboard.writeText(link);
                    setCopyLinkSuccess(true);
                    setTimeout(() => setCopyLinkSuccess(false), 2000);
                  }}
                  className="copy-submission-link-btn rounded-full px-6 h-12 text-base font-semibold transition-all duration-300 flex items-center gap-2 flex-1 sm:flex-none"
                  style={{
                    background: copyLinkSuccess ? PALETTE.inchworm : PALETTE.gunmetal,
                    color: PALETTE.brightSnow,
                    border: 'none',
                    borderRadius: '9999px'
                  }}
                >
                  {copyLinkSuccess ? (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      <span>Link Copied!</span>
                    </>
                  ) : (
                    <>
                      <Link2 className="w-4 h-4" />
                      <span>Copy Submission Link</span>
                    </>
                  )}
                </Button>

                {/* View Campaign Requirements Button - Secondary */}
                <Button
                  onClick={() => setShowBriefingModal(true)}
                  className="view-briefing-btn rounded-full px-6 h-12 text-base font-semibold transition-all duration-300 flex items-center gap-2"
                  style={{
                    background: PALETTE.brightSnow,
                    color: PALETTE.gunmetal,
                    border: `1px solid ${PALETTE.americanSilver}`,
                    borderRadius: '9999px'
                  }}
                >
                  <span>View Campaign Briefing</span>
                </Button>
              </div>

              {/* Usage Warning - Show when approaching limit */}
              <UsageWarning subscription={subscription} className="mb-4" />

              <div ref={gridContainerRef} className="ag-theme-quartz" style={{ 
                height: visibleSubmissions.length === 0 
                  ? '100px' 
                  : `${Math.min(600, 56 + visibleSubmissions.length * 40)}px`,
                width: '100%'
              }}>
                <style>{`
                  .ag-theme-quartz .ag-row {
                    border-bottom: none !important;
                  }
                  .ag-theme-quartz .ag-row-even {
                    border-bottom: none !important;
                  }
                  .ag-theme-quartz .ag-row-odd {
                    border-bottom: none !important;
                  }
                  .ag-theme-quartz .ag-row:hover {
                    background-color: rgba(209, 209, 209, 0.2) !important;
                  }
                  .ag-theme-quartz .ag-left-aligned-header {
                    text-align: left !important;
                  }
                  .ag-theme-quartz .ag-center-aligned-header {
                    text-align: center !important;
                  }
                  .ag-theme-quartz .ag-overlay-no-rows-center {
                    padding-top: 20px !important;
                  }
                  .ag-theme-quartz .ag-body-vertical-scroll {
                    opacity: 0;
                    transition: opacity 0.2s;
                  }
                  .ag-theme-quartz .ag-body-viewport:hover + .ag-body-vertical-scroll,
                  .ag-theme-quartz .ag-body-vertical-scroll:hover {
                    opacity: 1;
                  }
                  /* Hide sort order indicators and sort arrows */
                  .ag-theme-quartz .ag-sort-order {
                    display: none !important;
                  }
                  .ag-theme-quartz .ag-sort-indicator-icon {
                    display: none !important;
                  }
                  /* Tooltip styling */
                  .ag-theme-quartz .ag-tooltip {
                    background-color: ${PALETTE.brightSnow} !important;
                    color: ${PALETTE.gunmetal} !important;
                    border: 1px solid ${PALETTE.americanSilver} !important;
                    border-radius: 6px !important;
                    padding: 8px 12px !important;
                    font-size: 13px !important;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
                    max-width: 300px !important;
                  }
                `}</style>
                {gridMountReady && <AgGridReact
                  rowData={visibleSubmissions}
                  columnDefs={gridColumnDefs}
                  autoSizeStrategy={GRID_AUTO_SIZE_STRATEGY}
                  onGridReady={onGridReady}
                  defaultColDef={GRID_DEFAULT_COL_DEF}
                  initialState={GRID_INITIAL_STATE}
                  tooltipShowDelay={200}
                  tooltipHideDelay={2000}
                  pagination={false}
                  rowHeight={40}
                  suppressHorizontalScroll={true}
                  alwaysShowVerticalScroll={false}
                  suppressColumnVirtualisation={true}
                  overlayNoRowsTemplate={GRID_NO_ROWS_TEMPLATE}
                />}
              </div>
            </div>
          ) : (
            /* Campaign Creation Flow */
          <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Step Indicator */}
        <div className="flex items-center justify-center gap-4 mb-12 w-full max-w-4xl">
          {['Upload Briefing', 'Review Briefing', 'Submission Link'].map((label, idx) => {
            const stepIndex = ['upload', 'review', 'published'].indexOf(step);
            const isActive = stepIndex === idx;
            const isCompleted = stepIndex > idx;
            return (
              <React.Fragment key={label}>
                <div className="flex items-center flex-1 justify-center">
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center font-bold flex-shrink-0"
                    style={{
                      background: isActive ? PALETTE.gunmetal : (isCompleted ? PALETTE.inchworm : PALETTE.americanSilver),
                      color: isActive ? PALETTE.brightSnow : PALETTE.gunmetal
                    }}
                  >
                    {idx + 1}
                  </div>
                  <span 
                    className="ml-2 text-sm font-medium whitespace-nowrap hidden sm:inline"
                    style={{ color: isActive || isCompleted ? PALETTE.gunmetal : PALETTE.americanSilver }}
                  >
                    {label}
                  </span>
                </div>
                {idx < 2 && (
                  <div
                    className="h-0.5 mx-2 sm:mx-4 flex-1"
                    style={{
                      minWidth: 20,
                      background: isCompleted ? PALETTE.inchworm : PALETTE.americanSilver,
                    }}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Step 1: Upload Briefing */}
        {step === 'upload' && (
          <>
          <Card className="border-0 shadow-lg">
            <CardContent className="p-8">
                  {/* Tab System */}
                  <div className="flex gap-8 mb-6 border-b" style={{ borderColor: `${PALETTE.americanSilver}40` }}>
                    <button
                      onClick={() => setMode('upload')}
                      className="pb-3 px-1 text-base font-medium transition-colors relative"
                      style={{ color: mode === 'upload' ? PALETTE.gunmetal : `${PALETTE.gunmetal}50` }}
                    >
                      File Upload
                      {mode === 'upload' && (
                        <div className="absolute bottom-0 left-0 right-0 h-0.5" style={{ background: PALETTE.inchworm }} />
                      )}
                    </button>
                    <button
                      onClick={() => setMode('paste')}
                      className="pb-3 px-1 text-base font-medium transition-colors relative"
                      style={{ color: mode === 'paste' ? PALETTE.gunmetal : `${PALETTE.gunmetal}50` }}
                    >
                      Paste Text
                      {mode === 'paste' && (
                        <div className="absolute bottom-0 left-0 right-0 h-0.5" style={{ background: PALETTE.inchworm }} />
                      )}
                    </button>
                  </div>

                  <div className="space-y-6">
                    {mode === 'upload' ? (
                  <div className="relative">
                      <div
                        className="briefing-upload-dropzone border-2 border-dashed rounded-lg h-[320px] flex flex-col items-center justify-center text-center cursor-pointer transition-all hover:bg-gray-50/50"
                        style={{ borderColor: `${PALETTE.americanSilver}80`, background: '#F9F9F9' }}
                      onClick={() => !isProcessing && document.getElementById('briefing-file-input')?.click()}
                        onDragOver={(e) => {
                          e.preventDefault();
                        if (!isProcessing) {
                          e.currentTarget.style.borderColor = PALETTE.inchworm;
                        }
                        }}
                        onDragLeave={(e) => {
                          e.currentTarget.style.borderColor = `${PALETTE.americanSilver}80`;
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.currentTarget.style.borderColor = `${PALETTE.americanSilver}80`;
                        if (isProcessing) return;
                          const file = e.dataTransfer.files[0];
                          if (file) {
                            const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'];
                            if (validTypes.includes(file.type)) {
                              setSelectedFile(file);
                              setError(null);
                            } else {
                              setError('Please upload a PDF, Word document, or text file');
                            }
                          }
                        }}
                      >
                        <input
                          id="briefing-file-input"
                          type="file"
                          accept=".pdf,.doc,.docx,.txt"
                          onChange={handleFileSelect}
                          className="hidden"
                        />
                        
                        <Upload className="w-12 h-12 mx-auto mb-4" style={{ color: `${PALETTE.gunmetal}40`, strokeWidth: 1.5 }} />
                        <p className="text-lg font-semibold mb-3" style={{ color: PALETTE.gunmetal }}>
                          {selectedFile ? selectedFile.name : 'Drag and drop your file here'}
                        </p>
                        <p className="text-base mb-4" style={{ color: `${PALETTE.gunmetal}60` }}>
                          or{' '}
                          <span className="font-medium hover:underline cursor-pointer" style={{ color: PALETTE.inchworm }}>
                            Browse Files
                          </span>
                        </p>
                    </div>

                    {/* Spinning circle overlay */}
                    {isProcessing && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white/70 backdrop-blur-sm rounded-lg">
                        <div className="relative">
                          <div 
                            className="animate-spin rounded-full h-16 w-16"
                            style={{ 
                              border: `4px solid ${PALETTE.americanSilver}`,
                              borderTopColor: PALETTE.inchworm
                            }}
                          ></div>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <span className="text-sm font-semibold" style={{ color: PALETTE.gunmetal }}>
                              {timerProgress}%
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                      </div>
                    ) : (
                  <div className="relative">
                      <div className="briefing-paste-area border-2 border-dashed rounded-lg overflow-hidden h-[320px]" style={{ borderColor: `${PALETTE.americanSilver}80`, background: '#F9F9F9' }}>
                        <Textarea
                          value={pastedText}
                        onChange={(e) => !isProcessing && setPastedText(e.target.value)}
                          placeholder="Paste your campaign requirements here..."
                          className="h-full border-0 font-mono text-sm resize-none focus:outline-none focus:ring-0"
                          style={{ background: 'transparent', color: PALETTE.gunmetal }}
                        />
                      </div>

                    {/* Spinning circle overlay for text mode */}
              {isProcessing && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white/70 backdrop-blur-sm rounded-lg">
                        <div className="relative">
                          <div 
                            className="animate-spin rounded-full h-16 w-16"
                            style={{ 
                              border: `4px solid ${PALETTE.americanSilver}`,
                              borderTopColor: PALETTE.inchworm
                            }}
                          ></div>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <span className="text-sm font-semibold" style={{ color: PALETTE.gunmetal }}>
                              {timerProgress}%
                            </span>
                    </div>
                  </div>
                </div>
              )}
                  </div>
                )}
              </div>

              {error && !isProcessing && (
                <div className="mt-4 p-4 rounded-lg flex items-start space-x-3" style={{ background: `${PALETTE.orange}10` }}>
                  <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: PALETTE.orange }} />
                  <p className="text-sm" style={{ color: PALETTE.orange }}>{error}</p>
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Extract Requirements Button - Inside content container */}
          {!isProcessing && ((mode === 'upload' && selectedFile) || (mode === 'paste' && pastedText.trim())) && (
            <div className="mt-8 flex justify-center">
              <Button
                onClick={mode === 'upload' ? handleExtractFromFile : handleExtractFromText}
                disabled={isProcessing}
                className="extract-requirements-btn rounded-full px-8 h-12 text-base font-semibold transition-all duration-300 flex items-center gap-2"
                style={{ 
                  background: isProcessing ? PALETTE.americanSilver : PALETTE.inchworm,
                  color: PALETTE.gunmetal,
                  border: 'none',
                  borderRadius: '9999px'
                }}
              >
                <span className="text-base font-semibold">Extract Requirements</span>
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          )}
          </>
        )}

        {/* Step 2: Review & Edit Rules */}
        {step === 'review' && extractedData && (
          <>
          <Card className="border-0 shadow-lg">
            <CardContent className="p-8">
              <div className="mb-6">
                <h2 className="text-2xl font-bold mb-2" style={{ color: PALETTE.gunmetal }}>
                  Review & Edit Campaign Briefing
                </h2>
              </div>

              <div className="space-y-6">
                {/* Campaign Name */}
                <div>
                  <label className="block text-sm font-semibold mb-2" style={{ color: PALETTE.gunmetal }}>
                    Campaign Name <span className="text-red-500">*</span>
                  </label>
                    <Input
                    value={extractedData.campaign_name}
                    onChange={(e) => setExtractedData({ ...extractedData, campaign_name: e.target.value })}
                    placeholder="Enter campaign name"
                      className="campaign-name-input w-full h-12"
                  />
                </div>

                {/* Brand & Product Names */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold mb-2" style={{ color: PALETTE.gunmetal }}>
                      Brand Name
                    </label>
                    <Input
                      value={extractedData.brand_name}
                      onChange={(e) => setExtractedData({ ...extractedData, brand_name: e.target.value })}
                      placeholder="Enter brand name"
                      className="w-full h-12"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2" style={{ color: PALETTE.gunmetal }}>
                      Product Name
                    </label>
                    <Input
                      value={extractedData.product_name}
                      onChange={(e) => setExtractedData({ ...extractedData, product_name: e.target.value })}
                      placeholder="Enter product name"
                      className="w-full h-12"
                    />
                  </div>
                </div>

                {/* Brand & Product Image URLs */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <label className="block text-sm font-semibold" style={{ color: PALETTE.gunmetal }}>
                        Brand Logo URL
                      </label>
                      <button
                        onClick={() => setShowCategoryInfo('brand_logo')}
                        className="w-5 h-5 rounded-full flex items-center justify-center transition-colors hover:opacity-80"
                        style={{ background: `${PALETTE.americanSilver}40`, color: PALETTE.gunmetal }}
                      >
                        <HelpCircle className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <Input
                      value={extractedData.brand_logo_path || ''}
                      onChange={(e) => setExtractedData({ ...extractedData, brand_logo_path: e.target.value })}
                      placeholder="https://example.com/brand-logo.png"
                      className="brand-logo-url-input w-full h-12"
                    />
                    {extractedData.brand_logo_path && (
                      <div className="mt-2 p-2 rounded border" style={{ borderColor: `${PALETTE.americanSilver}60` }}>
                        <img 
                          src={extractedData.brand_logo_path} 
                          alt="Brand Logo Preview" 
                          className="max-h-20 object-contain"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <label className="block text-sm font-semibold" style={{ color: PALETTE.gunmetal }}>
                        Product Image URL
                      </label>
                      <button
                        onClick={() => setShowCategoryInfo('product_image')}
                        className="w-5 h-5 rounded-full flex items-center justify-center transition-colors hover:opacity-80"
                        style={{ background: `${PALETTE.americanSilver}40`, color: PALETTE.gunmetal }}
                      >
                        <HelpCircle className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <Input
                      value={extractedData.product_image_path || ''}
                      onChange={(e) => setExtractedData({ ...extractedData, product_image_path: e.target.value })}
                      placeholder="https://example.com/product.png"
                      className="product-image-url-input w-full h-12"
                    />
                    {extractedData.product_image_path && (
                      <div className="mt-2 p-2 rounded border" style={{ borderColor: `${PALETTE.americanSilver}60` }}>
                        <img 
                          src={extractedData.product_image_path} 
                          alt="Product Image Preview" 
                          className="max-h-20 object-contain"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Hooks */}
                <div className="requirements-section rounded-lg p-4" style={{ background: `${PALETTE.americanSilver}10` }}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                    <label className="text-sm font-semibold" style={{ color: PALETTE.gunmetal }}>
                      Hooks ({extractedData.hooks?.length || 0})
                    </label>
                      <button
                        onClick={() => setShowCategoryInfo('hooks')}
                        className="w-5 h-5 rounded-full flex items-center justify-center transition-colors hover:opacity-80"
                        style={{ background: `${PALETTE.americanSilver}40`, color: PALETTE.gunmetal }}
                      >
                        <HelpCircle className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setExtractedData({ ...extractedData, hooks: ['', ...(extractedData.hooks || [])] })}
                      style={{ color: PALETTE.inchworm }}
                      className="add-requirement-btn"
                    >
                      <Plus className="w-4 h-4 mr-1" /> Add
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {(extractedData.hooks || []).map((hook, idx) => (
                      <div key={idx} className="flex items-center space-x-2">
                        <Input
                          value={hook}
                          onChange={(e) => {
                            const newHooks = [...(extractedData.hooks || [])];
                            newHooks[idx] = e.target.value;
                            setExtractedData({ ...extractedData, hooks: newHooks });
                          }}
                          placeholder="Hook text"
                          className="flex-1 h-10"
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const newHooks = (extractedData.hooks || []).filter((_, i) => i !== idx);
                            setExtractedData({ ...extractedData, hooks: newHooks });
                          }}
                          className="delete-requirement-btn text-red-500 hover:bg-red-50"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Topics */}
                <div className="rounded-lg p-4" style={{ background: `${PALETTE.americanSilver}10` }}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                    <label className="text-sm font-semibold" style={{ color: PALETTE.gunmetal }}>
                      Topics ({extractedData.topics?.length || 0})
                    </label>
                      <button
                        onClick={() => setShowCategoryInfo('topics')}
                        className="w-5 h-5 rounded-full flex items-center justify-center transition-colors hover:opacity-80"
                        style={{ background: `${PALETTE.americanSilver}40`, color: PALETTE.gunmetal }}
                      >
                        <HelpCircle className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setExtractedData({ ...extractedData, topics: ['', ...(extractedData.topics || [])] })}
                      style={{ color: PALETTE.inchworm }}
                      className="add-requirement-btn"
                    >
                      <Plus className="w-4 h-4 mr-1" /> Add
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {(extractedData.topics || []).map((topic, idx) => (
                      <div key={idx} className="flex items-center space-x-2">
                        <Input
                          value={topic}
                          onChange={(e) => {
                            const newTopics = [...(extractedData.topics || [])];
                            newTopics[idx] = e.target.value;
                            setExtractedData({ ...extractedData, topics: newTopics });
                          }}
                          placeholder="Topic"
                          className="flex-1 h-10"
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const newTopics = (extractedData.topics || []).filter((_, i) => i !== idx);
                            setExtractedData({ ...extractedData, topics: newTopics });
                          }}
                          className="delete-requirement-btn text-red-500 hover:bg-red-50"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Call to Actions */}
                <div className="rounded-lg p-4" style={{ background: `${PALETTE.americanSilver}10` }}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                    <label className="text-sm font-semibold" style={{ color: PALETTE.gunmetal }}>
                      Call to Actions ({extractedData.call_to_actions?.length || 0})
                    </label>
                      <button
                        onClick={() => setShowCategoryInfo('call_to_actions')}
                        className="w-5 h-5 rounded-full flex items-center justify-center transition-colors hover:opacity-80"
                        style={{ background: `${PALETTE.americanSilver}40`, color: PALETTE.gunmetal }}
                      >
                        <HelpCircle className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setExtractedData({ ...extractedData, call_to_actions: ['', ...(extractedData.call_to_actions || [])] })}
                      style={{ color: PALETTE.inchworm }}
                      className="add-requirement-btn"
                    >
                      <Plus className="w-4 h-4 mr-1" /> Add
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {(extractedData.call_to_actions || []).map((cta, idx) => (
                      <div key={idx} className="flex items-center space-x-2">
                        <Input
                          value={cta}
                          onChange={(e) => {
                            const newCTAs = [...(extractedData.call_to_actions || [])];
                            newCTAs[idx] = e.target.value;
                            setExtractedData({ ...extractedData, call_to_actions: newCTAs });
                          }}
                          placeholder="CTA text"
                          className="flex-1 h-10"
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const newCTAs = (extractedData.call_to_actions || []).filter((_, i) => i !== idx);
                            setExtractedData({ ...extractedData, call_to_actions: newCTAs });
                          }}
                          className="delete-requirement-btn text-red-500 hover:bg-red-50"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Don'ts (Elements) */}
                <div className="rounded-lg p-4" style={{ background: `${PALETTE.americanSilver}10` }}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                    <label className="text-sm font-semibold" style={{ color: PALETTE.gunmetal }}>
                      Don'ts ({extractedData.elements?.length || 0})
                    </label>
                      <button
                        onClick={() => setShowCategoryInfo('elements')}
                        className="w-5 h-5 rounded-full flex items-center justify-center transition-colors hover:opacity-80"
                        style={{ background: `${PALETTE.americanSilver}40`, color: PALETTE.gunmetal }}
                      >
                        <HelpCircle className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setExtractedData({ ...extractedData, elements: ['', ...(extractedData.elements || [])] })}
                      style={{ color: PALETTE.inchworm }}
                      className="add-requirement-btn"
                    >
                      <Plus className="w-4 h-4 mr-1" /> Add
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {(extractedData.elements || []).map((element, idx) => (
                      <div key={idx} className="flex items-center space-x-2">
                        <Input
                          value={element}
                          onChange={(e) => {
                            const newElements = [...(extractedData.elements || [])];
                            newElements[idx] = e.target.value;
                            setExtractedData({ ...extractedData, elements: newElements });
                          }}
                          placeholder="Don't"
                          className="flex-1 h-10"
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const newElements = (extractedData.elements || []).filter((_, i) => i !== idx);
                            setExtractedData({ ...extractedData, elements: newElements });
                          }}
                          className="delete-requirement-btn text-red-500 hover:bg-red-50"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Story */}
                <div className="rounded-lg p-4" style={{ background: `${PALETTE.americanSilver}10` }}>
                  <div className="flex items-center gap-2 mb-2">
                    <label className="block text-sm font-semibold" style={{ color: PALETTE.gunmetal }}>
                    Story
                  </label>
                    <button
                      onClick={() => setShowCategoryInfo('story')}
                      className="w-5 h-5 rounded-full flex items-center justify-center transition-colors hover:opacity-80"
                      style={{ background: `${PALETTE.americanSilver}40`, color: PALETTE.gunmetal }}
                    >
                      <HelpCircle className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <Textarea
                    value={extractedData.story || ''}
                    onChange={(e) => setExtractedData({ ...extractedData, story: e.target.value })}
                    placeholder="Narrative structure and storyline"
                    className="w-full min-h-[100px]"
                  />
                </div>

                {/* Visuals & Background */}
                <div className="rounded-lg p-4" style={{ background: `${PALETTE.americanSilver}10` }}>
                  <div className="flex items-center gap-2 mb-2">
                    <label className="block text-sm font-semibold" style={{ color: PALETTE.gunmetal }}>
                    Visuals & Background
                  </label>
                    <button
                      onClick={() => setShowCategoryInfo('visuals_background')}
                      className="w-5 h-5 rounded-full flex items-center justify-center transition-colors hover:opacity-80"
                      style={{ background: `${PALETTE.americanSilver}40`, color: PALETTE.gunmetal }}
                    >
                      <HelpCircle className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <Textarea
                    value={extractedData.visuals_background || ''}
                    onChange={(e) => setExtractedData({ ...extractedData, visuals_background: e.target.value })}
                    placeholder="Visual style and setting requirements"
                    className="w-full min-h-[100px]"
                  />
                </div>

                {error && (
                  <div className="p-4 rounded-lg flex items-start space-x-3" style={{ background: `${PALETTE.orange}10` }}>
                    <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: PALETTE.orange }} />
                    <p className="text-sm" style={{ color: PALETTE.orange }}>{error}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
          
          {/* Navigation Buttons - Inside content container */}
          <div className="mt-8 flex justify-center">
            <div className="flex items-center gap-4">
              {/* Back Button */}
                  <Button
                    onClick={() => setStep('upload')}
                className="rounded-full px-6 h-12 text-base font-semibold transition-all duration-300 flex items-center gap-2"
                style={{ 
                  background: PALETTE.brightSnow,
                  color: PALETTE.gunmetal,
                  border: `1px solid ${PALETTE.americanSilver}`,
                  borderRadius: '9999px'
                }}
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="text-base font-semibold">Back</span>
                  </Button>
              
              {/* Publish Button */}
                  <Button
                    onClick={handlePublishCampaign}
                    disabled={isPublishing}
                className="publish-briefing-btn rounded-full px-8 h-12 text-base font-semibold transition-all duration-300 flex items-center gap-2"
                style={{ 
                  background: isPublishing ? PALETTE.americanSilver : PALETTE.inchworm,
                  color: PALETTE.gunmetal,
                  border: 'none',
                  borderRadius: '9999px'
                }}
              >
                <Link2 className="w-4 h-4" />
                <span className="text-base font-bold">{isPublishing ? 'Publishing...' : 'Generate Submission Link'}</span>                
                  </Button>
                </div>
              </div>
          </>
        )}

        {/* Step 3: Campaign Published */}
        {step === 'published' && (
          <Card className="border-0 shadow-lg">
            <CardContent className="p-8">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold mb-2" style={{ color: PALETTE.gunmetal }}>
                  Submission Link Generated!
                </h2>
                <p className="text-base" style={{ color: `${PALETTE.gunmetal}70` }}>
                  Share this link with your creators to submit videos
                </p>
              </div>

              <div className="space-y-6">
                {/* Campaign Summary */}
                <div className="p-4 rounded-lg" style={{ background: `${PALETTE.americanSilver}10` }}>
                  <div className="flex justify-between mb-2">
                    <span className="font-medium" style={{ color: PALETTE.gunmetal }}>Campaign:</span>
                    <span style={{ color: `${PALETTE.gunmetal}80` }}>{extractedData?.campaign_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium" style={{ color: PALETTE.gunmetal }}>Number of Requirements:</span>
                    <span style={{ color: `${PALETTE.gunmetal}80` }}>
                      {(() => {
                        let count = 0;
                        if (extractedData?.hooks) count += extractedData.hooks.filter(h => h.trim()).length;
                        if (extractedData?.topics) count += extractedData.topics.filter(t => t.trim()).length;
                        if (extractedData?.call_to_actions) count += extractedData.call_to_actions.filter(c => c.trim()).length;
                        if (extractedData?.elements) count += extractedData.elements.filter(e => e.trim()).length;
                        if (extractedData?.story?.trim()) count += 1;
                        if (extractedData?.visuals_background?.trim()) count += 1;
                        return `${count} rules`;
                      })()}
                    </span>
                  </div>
                </div>

                {/* Campaign Link */}
                <div className="flex justify-center">
                    <Button
                      onClick={handleCopyLink}
                    className="submission-link-copy-btn rounded-full px-6 h-12 text-base font-semibold transition-all duration-300 flex items-center gap-2"
                    style={{
                      background: copySuccess ? PALETTE.inchworm : PALETTE.gunmetal,
                      color: PALETTE.brightSnow,
                      border: 'none',
                      borderRadius: '9999px'
                    }}
                  >
                    {copySuccess ? (
                      <>
                        <CheckCircle className="w-4 h-4" />
                        <span>Link Copied!</span>
                      </>
                    ) : (
                      <>
                        <Link2 className="w-4 h-4" />
                        <span>Copy Submission Link</span>
                      </>
                    )}
                    </Button>
                </div>

              </div>
            </CardContent>
          </Card>
        )}
            </div>
          )}
        </main>
                </div>


      {/* Approval Modal */}
      {showApprovalModal && selectedSubmission && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowApprovalModal(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <h2 className="text-2xl font-bold" style={{ color: PALETTE.gunmetal }}>
                  Approve Submission
                </h2>
              </div>
              <button
                onClick={() => setShowApprovalModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4 mb-6">
              <div className="flex items-start justify-between gap-6 px-6">
                <div className="text-sm" style={{ color: PALETTE.gunmetal }}>
                  <div className="flex">
                    <span className="font-medium inline-block w-24">Creator:</span>
                    <span className="font-medium">{selectedSubmission?.creator_first_name} {selectedSubmission?.creator_surname}</span>
                  </div>
                  <div className="flex">
                    <span className="font-medium inline-block w-24">E-Mail:</span>
                    <span className="font-medium">{selectedSubmission?.creator_email}</span>
                  </div>
                </div>
                <div className="flex items-center justify-end relative">
                  <button
                  type="button"
                  onClick={() => setApprovalLanguageOpen((prev) => !prev)}
                  className="rounded-full border px-4 py-2 text-sm bg-white shadow-sm flex items-center gap-2 transition-all hover:shadow-md hover:opacity-90"
                    style={{ borderColor: PALETTE.americanSilver, color: PALETTE.gunmetal }}
                  >
                  <img 
                    src={
                      approvalLanguage === 'en' ? 'https://flagcdn.com/w40/us.png' :
                      approvalLanguage === 'de' ? 'https://flagcdn.com/w40/de.png' :
                      approvalLanguage === 'fr' ? 'https://flagcdn.com/w40/fr.png' :
                      approvalLanguage === 'es' ? 'https://flagcdn.com/w40/es.png' :
                      'https://flagcdn.com/w40/pt.png'
                    }
                    alt=""
                    className="w-6 h-6 rounded-full object-cover flex-shrink-0"
                  />
                  <span>
                    {approvalLanguage === 'en' && 'English'}
                    {approvalLanguage === 'de' && 'Deutsch'}
                    {approvalLanguage === 'fr' && 'French'}
                    {approvalLanguage === 'es' && 'Spanish'}
                    {approvalLanguage === 'pt' && 'Portuguese'}
                  </span>
                  <ChevronDown className="w-4 h-4" style={{ color: PALETTE.gunmetal }} />
                </button>
                {approvalLanguageOpen && (
                  <div
                    className="absolute right-0 top-12 w-48 rounded-xl border bg-white shadow-lg py-2 z-40"
                    style={{ borderColor: PALETTE.americanSilver }}
                  >
                    {[
                      { value: 'en', label: 'English', flagUrl: 'https://flagcdn.com/w40/us.png' },
                      { value: 'es', label: 'Spanish', flagUrl: 'https://flagcdn.com/w40/es.png' },
                      { value: 'de', label: 'Deutsch', flagUrl: 'https://flagcdn.com/w40/de.png' },
                      { value: 'fr', label: 'French', flagUrl: 'https://flagcdn.com/w40/fr.png' },
                      { value: 'pt', label: 'Portuguese', flagUrl: 'https://flagcdn.com/w40/pt.png' },
                    ].map((option) => (
                      <button
                        key={option.value}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors flex items-center gap-3"
                        style={{ color: PALETTE.gunmetal }}
                        onClick={() => {
                          setApprovalLanguage(option.value);
                          setApprovalLanguageOpen(false);
                        }}
                      >
                        <img 
                          src={option.flagUrl}
                          alt=""
                          className="w-6 h-6 rounded-full object-cover flex-shrink-0"
                        />
                        <span>{option.label}</span>
                      </button>
                    ))}
                  </div>
                )}
                </div>
              </div>

              <div className="p-6 rounded-lg" style={{ background: PALETTE.brightSnow }}>
                <div className="space-y-3 text-sm" style={{ color: `${PALETTE.gunmetal}90` }}>
                  <div>
                    <label className="block text-xs font-semibold mb-1" style={{ color: `${PALETTE.gunmetal}80` }}>
                      Subject
                    </label>
                    <div className="px-3 py-2">
                      <input
                        className="w-full text-sm bg-transparent border rounded-md px-3 py-2 focus:outline-none"
                        style={{ color: PALETTE.gunmetal, borderColor: PALETTE.americanSilver }}
                        value={approvalSubject}
                        onChange={(e) => setApprovalSubject(e.target.value)}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold mb-1" style={{ color: `${PALETTE.gunmetal}80` }}>
                      Email Text
                    </label>
                    <div className="px-3 py-2">
                      <Textarea
                        value={approvalBody}
                        onChange={(e) => setApprovalBody(e.target.value)}
                        className="w-full min-h-[360px] text-sm overflow-y-auto border rounded-md px-3 py-2 bg-transparent focus:outline-none"
                        style={{ resize: 'vertical', color: PALETTE.gunmetal, borderColor: PALETTE.americanSilver }}
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-start gap-2">
                    <button
                      className="text-xs px-4 py-2 rounded-full border transition-all hover:shadow-sm hover:opacity-90 min-w-[110px]"
                      style={{
                        borderColor: approvalCopySuccess ? PALETTE.inchworm : PALETTE.americanSilver,
                        color: PALETTE.gunmetal,
                        background: approvalCopySuccess ? PALETTE.inchworm : 'transparent',
                      }}
                      onClick={async () => {
                        const text = `Subject: ${approvalSubject}\n\n${approvalBody}`;
                        await navigator.clipboard.writeText(text);
                        setApprovalCopySuccess(true);
                        setTimeout(() => setApprovalCopySuccess(false), 1200);
                      }}
                    >
                      {approvalCopySuccess ? 'Copied!' : 'Copy Text'}
                    </button>
                    <button
                      className="text-xs px-4 py-2 rounded-full border hover:bg-gray-50 transition-all hover:shadow-sm hover:opacity-90 min-w-[110px]"
                      style={{ borderColor: PALETTE.americanSilver, color: PALETTE.gunmetal }}
                      onClick={() => {
                        const recipient = selectedSubmission?.creator_email || '';
                        const mailto = `mailto:${recipient}?subject=${encodeURIComponent(approvalSubject)}&body=${encodeURIComponent(approvalBody)}`;
                        window.location.href = mailto;
                      }}
                    >
                      Open in Email
                    </button>
                    <button
                      className="px-6 py-2 rounded-full text-sm font-medium transition-all hover:shadow-sm hover:opacity-90"
                      style={{
                        background: approvalSuccess ? PALETTE.inchworm : PALETTE.inchworm,
                        color: PALETTE.gunmetal,
                      }}
                      onClick={async () => {
                        try {
                          const token = localStorage.getItem('token');
                          const response = await fetch(`${API_ROOT}/approve-submission`, {
                            method: 'POST',
                            headers: {
                              'Authorization': `Bearer ${token}`,
                              'Content-Type': 'application/json',
                            },
                            body: JSON.stringify({
                              analysis_id: selectedSubmission.analysis_id,
                            }),
                          });
                          if (response.ok) {
                            setApprovalSuccess(true);
                            if (selectedCampaignUuid) {
                              await fetchCampaignData(selectedCampaignUuid);
                            }
                          } else {
                            const errorData = await response.json().catch(() => ({ detail: 'Failed to approve' }));
                            alert(errorData.detail || 'Failed to approve submission');
                          }
                        } catch (err) {
                          console.error('Error approving submission:', err);
                          alert('Failed to approve submission');
                        }
                      }}
                      disabled={approvalSuccess}
                    >
                      {approvalSuccess ? 'Approved' : 'Approve'}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3" />
          </div>
        </div>
      )}

      {/* Category Info Modal */}
      {showCategoryInfo && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowCategoryInfo(null)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div 
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ background: `${PALETTE.inchworm}20` }}
                >
                  <HelpCircle className="w-5 h-5" style={{ color: PALETTE.gunmetal }} />
                </div>
                <h2 className="text-xl font-bold" style={{ color: PALETTE.gunmetal }}>
                  {showCategoryInfo === 'hooks' && 'Hooks'}
                  {showCategoryInfo === 'topics' && 'Topics'}
                  {showCategoryInfo === 'call_to_actions' && 'Call to Actions'}
                  {showCategoryInfo === 'elements' && "Don'ts"}
                  {showCategoryInfo === 'story' && 'Story'}
                  {showCategoryInfo === 'visuals_background' && 'Visuals & Background'}
                  {showCategoryInfo === 'brand_logo' && 'Brand Logo URL'}
                  {showCategoryInfo === 'product_image' && 'Product Image URL'}
                </h2>
              </div>
              <button
                onClick={() => setShowCategoryInfo(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3">
              <p className="text-sm leading-relaxed" style={{ color: `${PALETTE.gunmetal}90` }}>
                {showCategoryInfo === 'hooks' && 'Hooks are attention-grabbing elements that creators should include in their videos to engage viewers. These are compelling statements, questions, or visual elements that draw attention and make the content more appealing.'}
                {showCategoryInfo === 'topics' && 'Topics are the main subjects or themes that should be covered in the video content. These help ensure creators address the key messages and themes relevant to your campaign.'}
                {showCategoryInfo === 'call_to_actions' && 'Call to Actions (CTAs) are specific instructions you want creators to include, such as asking viewers to follow social media accounts, use specific hashtags, visit websites, or take other desired actions.'}
                {showCategoryInfo === 'elements' && "Don'ts are elements, behaviors, or content that creators must avoid in their videos. These restrictions help ensure the content aligns with brand guidelines and avoids inappropriate or off-brand material."}
                {showCategoryInfo === 'story' && 'Story refers to the narrative structure and storyline that should be followed in the video. This helps creators understand the overall message, flow, and storytelling approach you want them to use.'}
                {showCategoryInfo === 'visuals_background' && 'Visuals & Background describes the visual style, setting, and aesthetic requirements for the video. This includes information about the desired background, color schemes, visual elements, and overall look and feel.'}
                {showCategoryInfo === 'brand_logo' && 'The Brand Logo URL should be a direct link to your brand\'s logo image file (PNG, JPG, or SVG format). This logo will be analyzed by us to verify that it appears in creator videos. Make sure the URL is publicly accessible and points directly to the image file, not a webpage.'}
                {showCategoryInfo === 'product_image' && 'The Product Image URL should be a direct link to the product image that must be featured in creator videos. This helps us verify that the correct product is being showcased. Ensure the URL is publicly accessible and points directly to the image file (PNG, JPG, or SVG), not a product page.'}
              </p>
            </div>

            <div className="flex justify-end mt-6">
              <Button
                onClick={() => setShowCategoryInfo(null)}
                style={{ background: PALETTE.inchworm, color: PALETTE.gunmetal }}
                className="rounded-full px-6 h-10"
              >
                Got it
                  </Button>
                </div>
              </div>
        </div>
      )}

      {/* Revision Modal */}
      {showRevisionModal && selectedSubmission && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowRevisionModal(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <h2 className="text-2xl font-bold" style={{ color: PALETTE.gunmetal }}>
                  Request Revision
                </h2>
          </div>
              <button
                onClick={() => setShowRevisionModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
      </div>

            <div className="space-y-4 mb-6">
              <div className="flex items-start justify-between gap-6 px-6">
                <div className="text-sm" style={{ color: PALETTE.gunmetal }}>
                  <div className="flex">
                    <span className="font-medium inline-block w-24">Creator:</span>
                    <span className="font-medium">{selectedSubmission?.creator_first_name} {selectedSubmission?.creator_surname}</span>
                  </div>
                  <div className="flex">
                    <span className="font-medium inline-block w-24">E-Mail:</span>
                    <span className="font-medium">{selectedSubmission?.creator_email}</span>
                  </div>
                </div>
                <div className="flex items-center justify-end relative">
                  <button
                    type="button"
                    onClick={() => setRevisionLanguageOpen((prev) => !prev)}
                    className="rounded-full border px-4 py-2 text-sm bg-white shadow-sm flex items-center gap-2 transition-all hover:shadow-md hover:opacity-90"
                    style={{ borderColor: PALETTE.americanSilver, color: PALETTE.gunmetal }}
                  >
                    <img 
                      src={
                        revisionLanguage === 'en' ? 'https://flagcdn.com/w40/us.png' :
                        revisionLanguage === 'de' ? 'https://flagcdn.com/w40/de.png' :
                        revisionLanguage === 'fr' ? 'https://flagcdn.com/w40/fr.png' :
                        revisionLanguage === 'es' ? 'https://flagcdn.com/w40/es.png' :
                        'https://flagcdn.com/w40/pt.png'
                      }
                      alt=""
                      className="w-6 h-6 rounded-full object-cover flex-shrink-0"
                    />
                    <span>
                      {revisionLanguage === 'en' && 'English'}
                      {revisionLanguage === 'de' && 'Deutsch'}
                      {revisionLanguage === 'fr' && 'French'}
                      {revisionLanguage === 'es' && 'Spanish'}
                      {revisionLanguage === 'pt' && 'Portuguese'}
                    </span>
                    <ChevronDown className="w-4 h-4" style={{ color: PALETTE.gunmetal }} />
                  </button>
                  {revisionLanguageOpen && (
                    <div
                      className="absolute right-0 top-12 w-48 rounded-xl border bg-white shadow-lg py-2 z-40"
                      style={{ borderColor: PALETTE.americanSilver }}
                    >
                      {[
                        { value: 'en', label: 'English', flagUrl: 'https://flagcdn.com/w40/us.png' },
                        { value: 'es', label: 'Spanish', flagUrl: 'https://flagcdn.com/w40/es.png' },
                        { value: 'de', label: 'Deutsch', flagUrl: 'https://flagcdn.com/w40/de.png' },
                        { value: 'fr', label: 'French', flagUrl: 'https://flagcdn.com/w40/fr.png' },
                        { value: 'pt', label: 'Portuguese', flagUrl: 'https://flagcdn.com/w40/pt.png' },
                      ].map((option) => (
                        <button
                          key={option.value}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors flex items-center gap-3"
                          style={{ color: PALETTE.gunmetal }}
                          onClick={() => {
                            setRevisionLanguage(option.value);
                            setRevisionLanguageOpen(false);
                          }}
                        >
                          <img 
                            src={option.flagUrl}
                            alt=""
                            className="w-6 h-6 rounded-full object-cover flex-shrink-0"
                          />
                          <span>{option.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="p-6 rounded-lg" style={{ background: PALETTE.brightSnow }}>
                <div className="space-y-3 text-sm" style={{ color: `${PALETTE.gunmetal}90` }}>
                  <div>
                    <label className="block text-xs font-semibold mb-1" style={{ color: `${PALETTE.gunmetal}80` }}>
                      Subject
                    </label>
                    <div className="px-3 py-2">
                      <input
                        className="w-full text-sm bg-transparent border rounded-md px-3 py-2 focus:outline-none"
                        style={{ color: PALETTE.gunmetal, borderColor: PALETTE.americanSilver }}
                        value={revisionSubject}
                        onChange={(e) => setRevisionSubject(e.target.value)}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold mb-1" style={{ color: `${PALETTE.gunmetal}80` }}>
                      Email Text
                    </label>                    
                    <div className="px-3 py-2">
                      <Textarea
                        value={revisionBody}
                        onChange={(e) => setRevisionBody(e.target.value)}
                        className="w-full min-h-[360px] text-sm overflow-y-auto border rounded-md px-3 py-2 bg-transparent focus:outline-none"
                        style={{ resize: 'vertical', color: PALETTE.gunmetal, borderColor: PALETTE.americanSilver }}
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-start gap-2">
                    <button
                      className="text-xs px-4 py-2 rounded-full border transition-all hover:shadow-sm hover:opacity-90 min-w-[110px]"
                      style={{
                        borderColor: revisionCopySuccess ? PALETTE.inchworm : PALETTE.americanSilver,
                        color: PALETTE.gunmetal,
                        background: revisionCopySuccess ? PALETTE.inchworm : 'transparent',
                      }}
                      onClick={async () => {
                        const text = `Subject: ${revisionSubject}\n\n${revisionBody}`;
                        await navigator.clipboard.writeText(text);
                        setRevisionCopySuccess(true);
                        setTimeout(() => setRevisionCopySuccess(false), 1200);
                      }}
                    >
                      {revisionCopySuccess ? 'Copied!' : 'Copy Text'}
                    </button>
                    <button
                      className="text-xs px-4 py-2 rounded-full border hover:bg-gray-50 transition-all hover:shadow-sm hover:opacity-90 min-w-[110px]"
                      style={{ borderColor: PALETTE.americanSilver, color: PALETTE.gunmetal }}
                      onClick={() => {
                        const recipient = selectedSubmission?.creator_email || '';
                        const mailto = `mailto:${recipient}?subject=${encodeURIComponent(revisionSubject)}&body=${encodeURIComponent(revisionBody)}`;
                        window.location.href = mailto;
                      }}
                    >
                      Open in Email
                    </button>
                    <button
                      className="px-6 py-2 rounded-full text-sm font-medium transition-all hover:shadow-sm hover:opacity-90"
                      style={{
                        background: revisionSuccess ? PALETTE.orange : PALETTE.orange,
                        color: PALETTE.brightSnow,
                      }}
                      onClick={async () => {
                        try {
                          const token = localStorage.getItem('token');
                          const response = await fetch(`${API_ROOT}/request-revision`, {
                            method: 'POST',
                            headers: {
                              'Authorization': `Bearer ${token}`,
                              'Content-Type': 'application/json',
                            },
                            body: JSON.stringify({
                              analysis_id: selectedSubmission.analysis_id,
                            }),
                          });
                          if (response.ok) {
                            setRevisionSuccess(true);
                            if (selectedCampaignUuid) {
                              await fetchCampaignData(selectedCampaignUuid);
                            }
                          } else {
                            const errorData = await response.json().catch(() => ({ detail: 'Failed to request revision' }));
                            alert(errorData.detail || 'Failed to request revision');
                          }
                        } catch (err) {
                          console.error('Error requesting revision:', err);
                          alert('Failed to request revision');
                        }
                      }}
                      disabled={revisionSuccess}
                    >
                      {revisionSuccess ? 'Revision Requested' : 'Revision'}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3" />
          </div>
        </div>
      )}

      {/* Briefing Requirements Modal */}
      {showBriefingModal && selectedCampaign && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowBriefingModal(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 p-8"
            onClick={(e) => e.stopPropagation()}
            style={{ maxHeight: '90vh', overflowY: 'auto' }}
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">                
                <div>
                  <h2 className="text-2xl font-bold" style={{ color: PALETTE.gunmetal }}>
                    Campaign Briefing
                  </h2>
                  <p className="text-sm" style={{ color: `${PALETTE.gunmetal}70` }}>
                    {selectedCampaign.campaign_name}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowBriefingModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Campaign Info */}
              <div className="p-4 rounded-lg" style={{ background: `${PALETTE.americanSilver}10` }}>
                <div className="grid grid-cols-2 gap-6">
                  {/* Brand Column */}
                  {selectedCampaign.brand_name && (
                    <div>
                      <p className="text-xs font-medium mb-1" style={{ color: `${PALETTE.gunmetal}60` }}>Brand</p>
                      <p className="text-sm font-semibold mb-3" style={{ color: PALETTE.gunmetal }}>{selectedCampaign.brand_name}</p>
                      
                      {/* Brand Logo under Brand Name */}
                      {selectedCampaign.brand_logo_path && (
                        <div className="mt-3">
                          <p className="text-xs font-medium mb-2" style={{ color: `${PALETTE.gunmetal}60` }}>Brand Logo</p>
                          <div className="p-2 rounded border flex items-center justify-center bg-white" style={{ borderColor: `${PALETTE.americanSilver}60`, minHeight: '80px' }}>
                            <img 
                              src={selectedCampaign.brand_logo_path} 
                              alt="Brand Logo" 
                              className="max-h-16 max-w-full object-contain"
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                                const parent = (e.target as HTMLElement).parentElement;
                                if (parent) {
                                  parent.innerHTML = '<p class="text-xs" style="color: #D1D1D1">Image unavailable</p>';
                                }
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Product Column */}
                  {selectedCampaign.product_name && (
                    <div>
                      <p className="text-xs font-medium mb-1" style={{ color: `${PALETTE.gunmetal}60` }}>Product</p>
                      <p className="text-sm font-semibold mb-3" style={{ color: PALETTE.gunmetal }}>{selectedCampaign.product_name}</p>
                      
                      {/* Product Image under Product Name */}
                      {selectedCampaign.product_image_path && (
                        <div className="mt-3">
                          <p className="text-xs font-medium mb-2" style={{ color: `${PALETTE.gunmetal}60` }}>Product Image</p>
                          <div className="p-2 rounded border flex items-center justify-center bg-white" style={{ borderColor: `${PALETTE.americanSilver}60`, minHeight: '80px' }}>
                            <img 
                              src={selectedCampaign.product_image_path} 
                              alt="Product Image" 
                              className="max-h-16 max-w-full object-contain"
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                                const parent = (e.target as HTMLElement).parentElement;
                                if (parent) {
                                  parent.innerHTML = '<p class="text-xs" style="color: #D1D1D1">Image unavailable</p>';
                                }
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Requirements */}
              {(() => {
                try {
                  // Get requirements data
                  let requirementsData = null;
                  
                  if (selectedCampaign.requirements_json) {
                    try {
                      requirementsData = JSON.parse(selectedCampaign.requirements_json);
                      console.log('📋 Parsed requirements_json:', requirementsData);
                      console.log('📋 Is Array:', Array.isArray(requirementsData));
                      console.log('📋 Is Object:', typeof requirementsData === 'object' && !Array.isArray(requirementsData));
                      if (typeof requirementsData === 'object' && !Array.isArray(requirementsData)) {
                        console.log('📋 Object keys:', Object.keys(requirementsData));
                      }
                    } catch (e) {
                      console.error('Error parsing requirements_json:', e);
                    }
                  }
                  
                  if (!requirementsData && selectedCampaign.requirements) {
                    requirementsData = selectedCampaign.requirements;
                  }
                  
                  if (!requirementsData) {
                    return (
                      <div className="p-6 rounded-lg text-center" style={{ background: `${PALETTE.americanSilver}10` }}>
                        <p className="text-sm" style={{ color: `${PALETTE.gunmetal}60` }}>
                          No requirements available
                        </p>
                      </div>
                    );
                  }
                  
                  // If it's a simple array (old format), display with note
                  if (Array.isArray(requirementsData)) {
                    const validReqs = requirementsData.filter(r => r && typeof r === 'string' && r.trim());
                    
                    if (validReqs.length === 0) {
                      return (
                        <div className="p-6 rounded-lg text-center" style={{ background: `${PALETTE.americanSilver}10` }}>
                          <p className="text-sm" style={{ color: `${PALETTE.gunmetal}60` }}>
                            No requirements available
                          </p>
                        </div>
                      );
                    }
                    
                    return (
                      <div>
                        <div className="p-3 rounded-lg mb-3" style={{ background: `${PALETTE.paleViolet}10`, border: `1px solid ${PALETTE.paleViolet}40` }}>
                          <p className="text-xs" style={{ color: `${PALETTE.gunmetal}80` }}>
                            ℹ️ This campaign was created with an older format. New campaigns will show requirements organized by category (Hooks, Topics, Call to Actions, Don'ts, Story, Visuals & Background).
                          </p>
                        </div>
                        <div className="p-4 rounded-lg" style={{ background: `${PALETTE.americanSilver}10` }}>
                          <h4 className="text-base font-semibold mb-3" style={{ color: PALETTE.gunmetal }}>
                            All Requirements ({validReqs.length})
                          </h4>
                          <div className="space-y-2">
                            {validReqs.map((req: string, idx: number) => (
                              <div 
                                key={idx}
                                className="flex items-start gap-2 text-sm leading-relaxed"
                                style={{ color: PALETTE.gunmetal }}
                              >
                                <span className="flex-shrink-0 mt-1.5 w-1 h-1 rounded-full" style={{ background: PALETTE.gunmetal }}></span>
                                <span className="flex-1">{req}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  }
                  
                  // If it's an object with categories
                  if (typeof requirementsData === 'object' && requirementsData !== null) {
                    // Category name mapping for display
                    const categoryDisplayNames: { [key: string]: string } = {
                      'hooks': 'Hooks',
                      'topics': 'Topics',
                      'call_to_actions': 'Call to Actions',
                      'elements': "Don'ts",
                      'story': 'Story',
                      'visuals_background': 'Visuals & Background'
                    };
                    
                    const categoryOrder = ['hooks', 'topics', 'call_to_actions', 'elements', 'story', 'visuals_background'];
                    
                    const categories = categoryOrder
                      .map(key => ({ key, value: requirementsData[key] }))
                      .filter(({ value }) => {
                        if (Array.isArray(value)) {
                          return value.some((item: any) => item && typeof item === 'string' && item.trim());
                        } else if (typeof value === 'string') {
                          return value.trim().length > 0;
                        }
                        return false;
                      });
                    
                    if (categories.length === 0) {
                      return (
                        <div className="p-6 rounded-lg text-center" style={{ background: `${PALETTE.americanSilver}10` }}>
                          <p className="text-sm" style={{ color: `${PALETTE.gunmetal}60` }}>
                            No requirements available
                          </p>
                        </div>
                      );
                    }
                    
                    return (
                      <div className="space-y-4">
                        {categories.map(({ key, value }) => {
                          const displayName = categoryDisplayNames[key] || key;
                          const isTextCategory = key === 'story' || key === 'visuals_background';
                          
                          return (
                            <div key={key} className="p-4 rounded-lg" style={{ background: `${PALETTE.americanSilver}10` }}>
                              {Array.isArray(value) ? (
                                <>
                                  <h4 className="text-base font-semibold mb-3" style={{ color: PALETTE.gunmetal }}>
                                    {displayName} ({value.filter((i: any) => i && i.trim()).length})
                                  </h4>
                                  <div className="space-y-2">
                                    {value.filter((item: any) => item && item.trim()).map((item: string, idx: number) => (
                                      <div 
                                        key={idx}
                                        className="flex items-start gap-2 text-sm leading-relaxed"
                                        style={{ color: PALETTE.gunmetal }}
                                      >
                                        <span className="flex-shrink-0 mt-1.5 w-1 h-1 rounded-full" style={{ background: PALETTE.gunmetal }}></span>
                                        <span className="flex-1">{item}</span>
                                      </div>
                                    ))}
                                  </div>
                                </>
                              ) : typeof value === 'string' && isTextCategory ? (
                                <>
                                  <h4 className="text-base font-semibold mb-3" style={{ color: PALETTE.gunmetal }}>
                                    {displayName}
                                  </h4>
                                  <p className="text-sm leading-relaxed" style={{ color: PALETTE.gunmetal }}>
                                    {value}
                                  </p>
                                </>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    );
                  }
                  
                  return (
                    <div className="p-6 rounded-lg text-center" style={{ background: `${PALETTE.americanSilver}10` }}>
                      <p className="text-sm" style={{ color: `${PALETTE.gunmetal}60` }}>
                        No requirements available
                      </p>
                    </div>
                  );
                } catch (error) {
                  console.error('Error displaying requirements:', error);
                  return (
                    <div className="p-6 rounded-lg text-center" style={{ background: `${PALETTE.americanSilver}10` }}>
                      <p className="text-sm" style={{ color: `${PALETTE.gunmetal}60` }}>
                        Error loading requirements
                      </p>
                    </div>
                  );
                }
              })()}
            </div>

            <div className="flex justify-end mt-6">
              <Button
                onClick={() => setShowBriefingModal(false)}
                className="rounded-full px-6 h-10"
                style={{ background: PALETTE.inchworm, color: PALETTE.gunmetal, borderRadius: '9999px' }}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Trial Welcome Modal */}
      {showTrialWelcome && (
        <div 
          className="fixed inset-0 z-[10000] flex items-center justify-center"
          style={{ background: 'rgba(0,0,0,0.5)' }}
        >
          <div 
            className="relative w-full max-w-md mx-4 rounded-2xl shadow-2xl overflow-hidden"
            style={{ background: PALETTE.brightSnow }}
          >
            {/* Close button */}
            <button
              onClick={() => handleTrialWelcomeDismiss(false)}
              className="absolute top-4 right-4 p-1 rounded-full hover:bg-gray-100 transition-colors"
            >
              <X className="w-4 h-4" style={{ color: PALETTE.americanSilver }} />
            </button>

            {/* Header accent bar */}
            <div className="h-1.5" style={{ background: PALETTE.inchworm }} />

            <div className="px-8 py-8 text-center">
              <div 
                className="inline-flex items-center justify-center w-14 h-14 rounded-full mb-5"
                style={{ background: `${PALETTE.inchworm}20` }}
              >
                <Zap className="w-7 h-7" style={{ color: PALETTE.inchworm }} />
              </div>

              <h2 className="text-xl font-bold mb-2" style={{ color: PALETTE.gunmetal }}>
                Welcome to your 14-day free trial!
              </h2>
              <p className="text-sm mb-6" style={{ color: `${PALETTE.gunmetal}90` }}>
                We're excited to have you here. Here's what's included:
              </p>

              <div className="flex flex-col gap-3 mb-8 text-left">
                <div className="flex items-center gap-3 px-4 py-3 rounded-lg" style={{ background: `${PALETTE.inchworm}08` }}>
                  <CheckCircle className="w-5 h-5 flex-shrink-0" style={{ color: PALETTE.inchworm }} />
                  <span className="text-sm" style={{ color: PALETTE.gunmetal }}>
                    <strong>30 video checks</strong> — upload briefs, share links, and let creators self-check
                  </span>
                </div>
                <div className="flex items-center gap-3 px-4 py-3 rounded-lg" style={{ background: `${PALETTE.inchworm}08` }}>
                  <CheckCircle className="w-5 h-5 flex-shrink-0" style={{ color: PALETTE.inchworm }} />
                  <span className="text-sm" style={{ color: PALETTE.gunmetal }}>
                    <strong>14 days</strong> — full access to all features, no restrictions
                  </span>
                </div>
                <div className="flex items-center gap-3 px-4 py-3 rounded-lg" style={{ background: `${PALETTE.inchworm}08` }}>
                  <CheckCircle className="w-5 h-5 flex-shrink-0" style={{ color: PALETTE.inchworm }} />
                  <span className="text-sm" style={{ color: PALETTE.gunmetal }}>
                    <strong>No credit card required</strong> — try everything risk-free
                  </span>
                </div>
              </div>

              <div className="flex gap-3 justify-center">
                <button
                  onClick={() => handleTrialWelcomeDismiss(false)}
                  className="px-5 py-2.5 rounded-lg text-sm font-medium transition-all hover:bg-gray-50"
                  style={{ color: PALETTE.gunmetal, border: `1px solid ${PALETTE.americanSilver}` }}
                >
                  Skip Tour
                </button>
                <button
                  onClick={() => handleTrialWelcomeDismiss(true)}
                  className="px-5 py-2.5 rounded-lg text-sm font-medium transition-all"
                  style={{ background: PALETTE.inchworm, color: PALETTE.gunmetal, border: `1px solid ${PALETTE.inchworm}` }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#A3E635'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = PALETTE.inchworm; }}
                >
                  Show me around →
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BrandCampaignManager;

